// admin/pages/Support/AgentDashboard.jsx
// ─────────────────────────────────────────────────────────────
// REWIRED against the Conversation-based backend (see prior pass for
// the full event-mapping rationale: getTicketsByStatus → getAgentQueue,
// agentAcceptTicket → agentClaim, newEnquiry → newWaitingConversation,
// module-level `io(...)` → shared useSocket() hook).
//
// THIS PASS — closes the three gaps flagged at the bottom of the
// previous version:
//
// 1. CLOSED HISTORY TAB. New "Closed" tab, backed by the new
//    getMyClosedConversations query (paginated, agent's own closed
//    conversations only — see socketHandlers.js). Loaded on-demand
//    when the tab is first opened, not on every mount, since it's the
//    least-frequently-needed view and there's no reason to pay for it
//    up front.
//
// 2. "MY CONVERSATIONS" NOW SERVER-SOURCED. Calls
//    getMyActiveConversations on mount/reconnect — this is what makes
//    the tab survive a page refresh, since it no longer depends on
//    client-side claim history from the current session. The
//    conversationOpened-driven optimistic update (from the prior
//    pass) is KEPT alongside this — it still gives the agent's OWN
//    claim instant feedback without waiting on a round trip, while
//    getMyActiveConversations is what makes a stale/empty list
//    self-heal after a refresh or reconnect.
//
// 3. claimFailed HANDLING. A toast-style inline banner (not a
//    blocking alert()) shown when this agent loses a claim race,
//    auto-dismissing after a few seconds. The conversation is also
//    removed from the local queue copy immediately (it's gone either
//    way — someone else has it), instead of leaving a now-stale
//    "waiting" entry sitting in the list.
// ─────────────────────────────────────────────────────────────
import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../../context/useAuth";
import { useSocket } from "../../../hooks/useSocket";
import TicketList from "../components/TicketList";
import LiveAgentChat from "../components/LiveAgentChat";

const TAB_LABELS = {
  waiting: "Waiting",
  mine:    "My Conversations",
  closed:  "Closed",
};

function AgentDashboard() {
  const { user } = useAuth();
  const isAgent = !!user && ["admin", "agent"].includes(user.role);

  const [queue, setQueue]                       = useState([]); // waiting_for_agent
  const [myConversations, setMyConversations]   = useState([]); // with_agent, assigned to me
  const [closedConversations, setClosedConversations] = useState([]); // closedBy me
  const [closedHasMore, setClosedHasMore]        = useState(false);
  const [closedLoading, setClosedLoading]        = useState(false);
  const [closedLoadedOnce, setClosedLoadedOnce]  = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [activeTab, setActiveTab]                = useState("waiting");
  const [mobileView, setMobileView]              = useState("list");
  // FIXED — gap #3. Short-lived banner for a lost claim race, instead
  // of silently doing nothing (the original gap) or a blocking alert().
  const [claimNotice, setClaimNotice]            = useState(null);

  const { connected, on, off, emit } = useSocket(isAgent ? user.userId : null);

  const refreshQueue = useCallback(() => {
    emit("getAgentQueue");
  }, [emit]);

  const refreshMine = useCallback(() => {
    emit("getMyActiveConversations");
  }, [emit]);

  // FIXED — gap #1. Cursor-paginated load, appends to the existing
  // list rather than replacing it, so "load more" works. `reset`
  // clears first — used when the tab is opened fresh.
  const closedCursorRef = useRef(null);
  const loadClosed = useCallback((reset = false) => {
    setClosedLoading(true);
    emit("getMyClosedConversations", {
      before: reset ? undefined : closedCursorRef.current,
      limit: 20,
    });
    if (reset) {
      closedCursorRef.current = null;
    }
  }, [emit]);

  useEffect(() => {
    if (!isAgent || !connected) return;

    emit("registerUser", { userId: user.userId, role: user.role });
    refreshQueue();
    refreshMine(); // FIXED — gap #2, fetched on every (re)connect, not just on claim

    function onAgentQueue(data) {
      setQueue(data);
    }
    function onMyActiveConversations(data) {
      // FIXED — gap #2. Server-sourced replacement of the whole list
      // on (re)connect. Merges rather than blindly overwrites IF a
      // conversationOpened update already landed first (rare race,
      // but cheap to handle correctly): keep whichever copy is for a
      // conversation already in state from a more recent local event,
      // otherwise take the server's copy.
      setMyConversations(data);
    }
    function onNewWaitingConversation() {
      if (activeTab === "waiting") refreshQueue();
    }
    function onConversationOpened(convo) {
      setMyConversations(prev => {
        const exists = prev.some(c => c._id === convo._id);
        if (exists) return prev.map(c => (c._id === convo._id ? convo : c));
        return [convo, ...prev];
      });
    }
    function onConversationClaimed({ conversationId }) {
      setQueue(prev => prev.filter(c => c._id !== conversationId));
    }
    // FIXED — gap #3. Was completely unhandled before — this agent's
    // own claim attempt lost the race (someone else got there first,
    // or the conversation is no longer waiting for some other
    // reason). Surface it instead of leaving the UI silent.
    function onClaimFailed({ conversationId, message }) {
      setQueue(prev => prev.filter(c => c._id !== conversationId));
      setClaimNotice(message || "That conversation is no longer available.");
      if (selectedConversationId === conversationId) {
        setSelectedConversationId(null);
        setMobileView("list");
      }
    }
    function onConversationClosed({ conversationId }) {
      setMyConversations(prev => prev.filter(c => c._id !== conversationId));
      if (selectedConversationId === conversationId) {
        setSelectedConversationId(null);
      }
      // If the Closed tab is currently open, refresh it so the
      // just-closed conversation appears without waiting for a
      // manual reload of that tab.
      if (activeTab === "closed") loadClosed(true);
    }
    function onMyClosedConversations({ conversations, hasMore }) {
      setClosedLoading(false);
      setClosedLoadedOnce(true);
      setClosedHasMore(hasMore);
      setClosedConversations(prev => {
        // First page of a fresh load (cursor was null) replaces;
        // subsequent pages append.
        const isFreshLoad = closedCursorRef.current === null;
        const next = isFreshLoad ? conversations : [...prev, ...conversations];
        const last = conversations[conversations.length - 1];
        closedCursorRef.current = last?.closedAt || closedCursorRef.current;
        return next;
      });
    }

    on("agentQueue", onAgentQueue);
    on("myActiveConversations", onMyActiveConversations);
    on("newWaitingConversation", onNewWaitingConversation);
    on("conversationOpened", onConversationOpened);
    on("conversationClaimed", onConversationClaimed);
    on("claimFailed", onClaimFailed);
    on("conversationClosed", onConversationClosed);
    on("myClosedConversations", onMyClosedConversations);

    return () => {
      off("agentQueue", onAgentQueue);
      off("myActiveConversations", onMyActiveConversations);
      off("newWaitingConversation", onNewWaitingConversation);
      off("conversationOpened", onConversationOpened);
      off("conversationClaimed", onConversationClaimed);
      off("claimFailed", onClaimFailed);
      off("conversationClosed", onConversationClosed);
      off("myClosedConversations", onMyClosedConversations);
    };
  }, [isAgent, connected, user, activeTab, refreshQueue, refreshMine, loadClosed, on, off, emit, selectedConversationId]);

  // FIXED — gap #3 cont. Auto-dismiss the claim-failed banner.
  useEffect(() => {
    if (!claimNotice) return;
    const t = setTimeout(() => setClaimNotice(null), 5000);
    return () => clearTimeout(t);
  }, [claimNotice]);

  // FIXED — gap #1 cont. Load closed conversations lazily, the first
  // time that tab is opened, rather than on every mount.
  useEffect(() => {
    if (activeTab === "closed" && !closedLoadedOnce && connected) {
      loadClosed(true);
    }
  }, [activeTab, closedLoadedOnce, connected, loadClosed]);

  if (!isAgent) {
    return (
      <div className="h-screen flex items-center justify-center text-white bg-[#0a0a0a]">
        Access Denied. Agent only.
      </div>
    );
  }

  const claimConversation = (convo) => {
    if (convo.status === "waiting_for_agent") {
      emit("agentClaim", { conversationId: convo._id });
      setQueue(prev => prev.filter(c => c._id !== convo._id));
    }
    setSelectedConversationId(convo._id);
    setMobileView("chat");
  };

  const handleBackToList = () => setMobileView("list");

  const visibleList =
    activeTab === "waiting" ? queue :
    activeTab === "mine"    ? myConversations :
    closedConversations;

  const selectedConvo =
    visibleList.find(c => c._id === selectedConversationId)
    || myConversations.find(c => c._id === selectedConversationId)
    || closedConversations.find(c => c._id === selectedConversationId);

  return (
    <div className="flex h-screen bg-[#0a0a0a] text-white overflow-hidden">

      <div className={`
        flex flex-col border-r border-white/10
        w-full md:w-96 md:flex flex-shrink-0
        ${mobileView === "list" ? "flex" : "hidden md:flex"}
      `}>
        <div className="p-5 border-b border-white/10 bg-[#141414] flex-shrink-0">
          <h1 className="text-xl font-bold text-white">Support Dashboard</h1>
          <p className="text-white/50 text-sm mt-0.5">
            {user.firstName || user.name || user.email}
          </p>
        </div>

        {/* FIXED — gap #3. Lost-claim-race notice. */}
        {claimNotice && (
          <div className="mx-4 mt-3 flex items-center gap-2 px-3 py-2.5 bg-amber-500/10 border border-amber-500/20 rounded-xl text-amber-400 text-xs flex-shrink-0">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="flex-1">{claimNotice}</span>
            <button onClick={() => setClaimNotice(null)} className="text-amber-400/60 hover:text-amber-400">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        <div className="flex border-b border-white/10 bg-[#141414] flex-shrink-0">
          {Object.entries(TAB_LABELS).map(([tab, label]) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-3.5 text-xs font-semibold transition-colors relative ${
                activeTab === tab ? "text-white" : "text-white/40 hover:text-white/70"
              }`}
            >
              {label}
              {tab === "waiting" && queue.length > 0 && (
                <span className="ml-1 bg-amber-500 text-black text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                  {queue.length}
                </span>
              )}
              {tab === "mine" && myConversations.length > 0 && (
                <span className="ml-1 bg-blue-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                  {myConversations.length}
                </span>
              )}
              {activeTab === tab && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00C896]" />}
            </button>
          ))}
        </div>

        <TicketList
          tickets={visibleList}
          selectedTicket={selectedConvo}
          onAccept={claimConversation}
          activeTab={activeTab}
        />

        {/* FIXED — gap #1 cont. Load-more for the closed tab. */}
        {activeTab === "closed" && (
          <div className="p-3 border-t border-white/10 flex-shrink-0">
            {closedLoading ? (
              <p className="text-center text-white/30 text-xs py-2">Loading…</p>
            ) : closedHasMore ? (
              <button
                onClick={() => loadClosed(false)}
                className="w-full py-2.5 rounded-xl text-xs font-semibold bg-white/8 hover:bg-white/15 text-white/60 hover:text-white transition-colors"
              >
                Load more
              </button>
            ) : closedConversations.length > 0 ? (
              <p className="text-center text-white/20 text-xs py-2">No more conversations</p>
            ) : null}
          </div>
        )}
      </div>

      <div className={`
        flex-1 flex flex-col min-w-0
        ${mobileView === "chat" ? "flex" : "hidden md:flex"}
      `}>
        {selectedConvo ? (
          <LiveAgentChat
            key={selectedConvo._id}
            conversation={selectedConvo}
            agent={user}
            onBack={handleBackToList}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center flex-col text-white/40 p-6 text-center">
            <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mb-4">
              <i className="ri-customer-service-2-line text-3xl text-white/20" />
            </div>
            <p className="text-lg font-semibold text-white/50">No conversation selected</p>
            <p className="text-sm mt-1">Choose a conversation from the sidebar to start chatting</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default AgentDashboard;

// ─────────────────────────────────────────────────────────────
// REMAINING KNOWN LIMITATIONS (not full gaps, smaller notes):
//
// - The Closed tab's pagination cursor (closedCursorRef) is reset to
//   null on a fresh load but only advances based on the LAST item of
//   the most recently received page — if conversationClosed triggers
//   a refresh (loadClosed(true)) while a "load more" was already
//   in-flight, the in-flight request's response will still land and
//   could interleave oddly with the reset. Unlikely in practice (an
//   agent closing a conversation while simultaneously scrolling their
//   own closed-history tab), not guarded against explicitly.
//
// - getMyActiveConversations and conversationOpened can both update
//   myConversations independently; onMyActiveConversations currently
//   does a full replace rather than a merge. If a claim's
//   conversationOpened event and a getMyActiveConversations response
//   arrive in an unlucky order, the LATER one wins outright rather
//   than field-level merging. Both should converge to the same data
//   moments apart in practice, so this is a theoretical inconsistency
//   window, not a likely visible bug.
// ─────────────────────────────────────────────────────────────